<template>
  <div class="form-item-repeatable-table">
    <div
      v-if="hasGroupLabel"
      class="form-item-table-title aisea-text-16-700-dark-gray"
    >
      {{ formatLabel(group.label) }}
    </div>
    <div class="form-item-table-wrapper">
      <table class="form-item-table-content">
        <thead>
          <tr>
            <th
              v-for="col in templateItems"
              :key="`col-${col.id}`"
              class="form-item-table-col-header aisea-text-14-700-dark-gray"
              :class="{ required: col.required && isEditing }"
            >
              {{ formatLabel(col.label) }}
            </th>
            <th
              v-if="isEditing && rowAddable"
              class="form-item-table-action-header"
            ></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="`row-${row.repeatIndex}`">
            <td
              v-for="col in templateItems"
              :key="`cell-${row.repeatIndex}-${col.id}`"
              class="form-item-table-cell"
            >
              <!-- 閲覧モード（編集中でも列単位 editable=false は read-only 表示） -->
              <div
                v-if="!isEditing || !isColEditable(col)"
                class="cell-display aisea-text-14-400-dark-gray"
              >
                <form-item-file-cell
                  v-if="isFileColumn(col)"
                  :valueFiles="getCellValueFiles(col, row)"
                  :enterpriseId="enterpriseId"
                  :reportId="reportId"
                  :formVersionItemId="col.id"
                  :repeatIndex="row.repeatIndex"
                />
                <template v-else>{{ getCellDisplayValue(col, row) }}</template>
              </div>
              <!-- 編集モード -->
              <form-item-table-cell
                v-else
                :enterpriseFormVersionItem="col"
                :reportFormVersionItem="getCellReportItem(row, col)"
                :reportFormVersion="reportFormVersion"
                :updatable="updatable"
                :isEditing="isEditing"
                :reportFormVersionId="reportFormVersionId"
                :repeatIndex="row.repeatIndex"
              />
            </td>
            <td
              v-if="isEditing && rowAddable"
              class="form-item-table-action-cell"
            >
              <button
                type="button"
                class="repeatable-row-btn"
                :disabled="!updatable"
                @click="removeRow(row)"
              >
                削除
              </button>
            </td>
          </tr>
          <tr v-if="rows.length === 0 && (!isEditing || !rowAddable)">
            <td
              :colspan="templateItems.length"
              class="cell-display aisea-text-14-400-dark-gray"
            >
              -
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="isEditing && rowAddable" class="repeatable-add-wrapper">
      <button
        type="button"
        class="repeatable-row-btn repeatable-add-btn"
        :disabled="!updatable"
        @click="addRow"
      >
        ＋ 行を追加
      </button>
    </div>
  </div>
</template>

<script>
import isString from "lodash/isString";
import enterpriseFormService from "@/services/enterprises/enterpriseFormService";
import operationReportService from "@/services/operations/operationReportService";
import { applyUnit, formatReportValueForDisplay } from "@/utils/operations";
import FormItemTableCell from "./FormItemTableCell";
import FormItemFileCell from "./FormItemFileCell";
import AuthService from "@/services/authService";
import { createNamespacedHelpers, mapGetters } from "vuex";
const { mapActions } = createNamespacedHelpers("operationReport");

// 可変行テーブル（form_version_item_groups.row_repeatable=true）。
// グループ配下の items を 1 行分のテンプレ列定義として扱い、値側の repeat_index で行数を可変にする。
export default {
  name: "FormItemRepeatableTable",
  components: { FormItemTableCell, FormItemFileCell },
  props: {
    group: { type: Object, required: true },
    items: { type: Array, required: true },
    reportFormVersion: { type: Object, default: () => ({}) },
    isEditing: { type: Boolean, default: false },
    updatable: { type: Boolean, default: false },
    // 書き込み先 ao_enterprise_vessel_report_form_version.id
    reportFormVersionId: { type: String, required: true },
  },
  data() {
    return { enterpriseId: "" };
  },
  computed: {
    ...mapGetters({
      masterMaps: "operationMasters/masterMaps",
      reportId: "operationReport/reportId",
    }),
    // テンプレ列（描画対象のみ。BINDING 等は除外）
    templateItems() {
      return (this.items || []).filter(item =>
        enterpriseFormService.isRenderableInReport(item)
      );
    },
    templateItemIds() {
      return this.templateItems.map(item => item.id);
    },
    reportItems() {
      const rfv = this.reportFormVersion || {};
      return rfv.ao_enterprise_vessel_report_form_version_items || [];
    },
    hasGroupLabel() {
      return isString(this.group.label) && this.group.label.length > 0;
    },
    // 行追加・削除を許可するか。BE の row_addable_on_edit に従う（既定 true）。
    // false の場合は編集中でも行の増減 UI（追加ボタン/削除列）を出さない（例: 輸送実績の荷役行は
    // 次航連絡からコピーされ行固定）。列ごとのセル編集可否（editable）とは独立。
    rowAddable() {
      return enterpriseFormService.isRowAddableOnEdit(this.group);
    },
    // 値を repeat_index でグルーピングして行配列に変換（repeat_index 昇順）
    rows() {
      const ids = new Set(this.templateItemIds);
      const byIndex = {};
      for (const item of this.reportItems) {
        if (!item || !ids.has(item.form_version_item_id)) continue;
        if (item.repeat_index == null) continue;
        const ri = item.repeat_index;
        if (!byIndex[ri]) byIndex[ri] = { repeatIndex: ri, byItemId: {} };
        byIndex[ri].byItemId[item.form_version_item_id] = item;
      }
      return Object.values(byIndex).sort(
        (a, b) => a.repeatIndex - b.repeatIndex
      );
    },
  },
  async created() {
    try {
      const user = await AuthService.getUser();
      this.enterpriseId = user && user.id ? user.id : "";
    } catch {
      this.enterpriseId = "";
    }
  },
  methods: {
    ...mapActions({
      addRepeatableRow: "addRepeatableRow",
      removeRepeatableRow: "removeRepeatableRow",
    }),

    formatLabel(label) {
      if (!isString(label)) return label;
      return label.replace(/\\n/g, "\n");
    },

    getCellReportItem(row, col) {
      return (
        row.byItemId[col.id] || {
          form_version_item_id: col.id,
          repeat_index: row.repeatIndex,
        }
      );
    },

    // 画像/ファイル列か（read 表示でファイル名クリック→ダウンロードのセルにする）
    isFileColumn(col) {
      return enterpriseFormService.isSingleImageFile(col);
    },

    // 列単位 editable=false（例: 輸送実績の港/貨物列）は編集中でも read-only 表示にする
    isColEditable(col) {
      return enterpriseFormService.isItemEditable(col);
    },

    // セルの value_files（ファイル配列）を取得
    getCellValueFiles(col, row) {
      const reportItem = this.getCellReportItem(row, col);
      return Array.isArray(reportItem.value_files)
        ? reportItem.value_files
        : [];
    },

    getCellDisplayValue(col, row) {
      const reportItem = this.getCellReportItem(row, col);
      const value = operationReportService.getValueFromReportFormVersionItem(
        reportItem
      );
      const hasValue = operationReportService.hasReportFormVersionItemValue(
        reportItem
      );
      if (!hasValue) return "-";

      // SELECT / RADIO は value をラベルへ変換
      const isSelect = enterpriseFormService.isSelect(col);
      const isRadioButton = enterpriseFormService.isRadioButton(col);
      let displayValue;
      if (
        (isSelect || isRadioButton) &&
        value != null &&
        Array.isArray(col.select_options) &&
        col.select_options.length > 0
      ) {
        const optionMap = col.select_options.reduce((acc, option) => {
          if (option && option.value != null) acc[option.value] = option;
          return acc;
        }, {});
        const option = optionMap[value];
        displayValue =
          option && (option.label || option.text)
            ? option.label || option.text
            : value;
      } else {
        displayValue = formatReportValueForDisplay(col, value, this.masterMaps);
      }
      return applyUnit(displayValue, col);
    },

    addRow() {
      if (!this.updatable || !this.rowAddable) return;
      const repeatIndex = this.rows.length
        ? Math.max(...this.rows.map(r => r.repeatIndex)) + 1
        : 0;
      this.addRepeatableRow({
        reportFormVersionId: this.reportFormVersionId,
        formVersionItemIds: this.templateItemIds,
        repeatIndex,
      });
    },

    removeRow(row) {
      if (!this.updatable || !this.rowAddable) return;
      this.removeRepeatableRow({
        reportFormVersionId: this.reportFormVersionId,
        formVersionItemIds: this.templateItemIds,
        repeatIndex: row.repeatIndex,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

.form-item-repeatable-table {
  grid-column: 1 / -1;
  margin: 8px 0;

  .form-item-table-title {
    margin-bottom: 8px;
  }

  .form-item-table-wrapper {
    overflow-x: auto;
  }

  .form-item-table-content {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid $aisea-light-gray;

    th,
    td {
      border: 1px solid $aisea-light-gray;
      padding: 8px 12px;
      text-align: left;
      vertical-align: middle;
      word-break: break-word;
      overflow-wrap: break-word;
    }

    thead th {
      background-color: #f5f7fa;
      white-space: pre-line;
      text-align: center;

      &.required:after {
        content: " *";
        font-size: 12px;
        color: $aisea-red;
        font-weight: 700;
      }
    }

    .form-item-table-cell {
      min-width: 120px;
      white-space: normal;
      word-break: break-word;
      overflow-wrap: break-word;

      .cell-display {
        min-height: 24px;
      }
    }

    .form-item-table-action-header {
      width: 64px;
      background-color: #f5f7fa;
    }

    .form-item-table-action-cell {
      text-align: center;
    }
  }

  .repeatable-row-btn {
    padding: 6px 12px;
    border: 1px solid $aisea-light-gray;
    border-radius: 6px;
    font-size: 14px;
    background: #fff;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  .repeatable-add-wrapper {
    margin-top: 8px;
  }
}
</style>
